import dayjs from "dayjs";

import React, { Fragment, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import { renderWeekPickerDay } from './utils/DatePickerUtils';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { getPickersLocale } from './utils/DatePickerLocale';

import QVActionBarWithTime from './QVActionBarWithTime';

import DateTimeFormatConfig from '../../../utils/DateTimeFormatConfig';
import RecurrenceDays from './recurringIntervalSelector/utils/RecurrenceDays';
import { ShopDatesInterval } from '../../../utils/ShopDatesInterval';

import { 
    selectStartPickerEnabledIntervals, 
    selectEndPickerEnabledIntervals,
    selectStartPickerEnabledHoursForDay,
    selectEndPickerEnabledHoursForDay,
    setFromPickerError,
    setToPickerError
} from '../../../store/filtersSlice';
import { selectWorkingHours, selectSemesterSettings, selectDaysToDisableAfterToday } from '../../../store/settingsSlice';


export default function DateTimePickerWithSlider(props) {
    const dispatch = useDispatch();
    const { i18n } = useTranslation();
    const pickersLocale = getPickersLocale(i18n.language);

    const { label, selectedDate, onChange, minDateTime, maxDate, isStartDatePicker, withAvailability = false, availabilityCalendar, onMonthChanged, withTime = true, openingTimesForStartDate = false, openingTimesForEndDate = false } = props;

    const startDatePickerEnabledIntervals = useSelector(selectStartPickerEnabledIntervals);
    const endDatePickerEnabledIntervals = useSelector(selectEndPickerEnabledIntervals);

    const startDatePickerEnabledHoursForDay = useSelector(selectStartPickerEnabledHoursForDay);
    const endDatePickerEnabledHoursForDay = useSelector(selectEndPickerEnabledHoursForDay);

    const todayHours = isStartDatePicker ? startDatePickerEnabledHoursForDay : endDatePickerEnabledHoursForDay;
    const openingTimes = isStartDatePicker ? openingTimesForStartDate : openingTimesForEndDate;
    
    const workingHours = useSelector(selectWorkingHours);
    const semesterSettings = useSelector(selectSemesterSettings);
    const daysToDisableAfterToday = useSelector(selectDaysToDisableAfterToday);

    const [isDateAvailable, setIsDateAvailable] = useState(true);
    const [open, setOpen] = useState(false);

    const shouldDisableDate = (dayToCheck) => {
        let shouldDisableDay = true;

        if (maxDate && dayToCheck.isAfter(maxDate, 'day')) {
            return shouldDisableDay;
        }

        // if x days after today should be disabled, also disable today
        if (daysToDisableAfterToday > 0 && dayToCheck.isSame(dayjs(), 'day')) {
            return shouldDisableDay
        }

        let formattedDayToCheck = DateTimeFormatConfig.formatDateToDateOnlyString(dayToCheck);
        const intervalsInfo = isStartDatePicker ? startDatePickerEnabledIntervals : endDatePickerEnabledIntervals;

        var intervalToCheck = null;

        intervalsInfo.every(interval => {
            if(dayToCheck.isBetween(DateTimeFormatConfig.getDateFromDateOnlyString(interval.from), 
                DateTimeFormatConfig.getDateFromDateOnlyString(interval.to), "d", '[]')) {

                shouldDisableDay = false;
                if(interval.disabledInside.includes(formattedDayToCheck)) {
                    shouldDisableDay = true;
                }

                intervalToCheck = interval.disabledInside;

                return false; // callback to every - stop Search
            }

            return true; // callback to every - stop Search
        });

        if(intervalToCheck && !shouldDisableDay) {
            shouldDisableDay = ShopDatesInterval.isDayInTheNextXAvailableDaysFromTomorrow(dayToCheck, intervalToCheck, daysToDisableAfterToday);
        }
        
        return shouldDisableDay;
    }

    const customRenderWeekPickerDay = (pickersDayProps) => {
        return renderWeekPickerDay(pickersDayProps, withAvailability, availabilityCalendar, shouldDisableDate, null, null, {
            availabilityDisplayMode: 'segments'
        });
    }

    const onTimeChanged = (hours, minutes) => {
        const newValue = selectedDate.hour(hours).minute(minutes);
        onChange(newValue);
    }

    const onDayChanged = (newValue) => {
        onChange(newValue);
    }

    const handleMonthChanged = (newMonth) => {
        if(onMonthChanged) {
            onMonthChanged(newMonth);
        }
    }

    const selectedDateAvailability = availabilityCalendar?.[selectedDate.format("YYYY-MM-DD")];

    let component = withTime && DateTimeFormatConfig.enableHours() && todayHours && todayHours.length > 0 ? {
        actionBar: QVActionBarWithTime,
        day: customRenderWeekPickerDay
    } : {
        day: customRenderWeekPickerDay
    };

    useEffect(() => {
        let isDateAvailable = ShopDatesInterval.isDateAvailable(workingHours, semesterSettings, selectedDate, isStartDatePicker ? RecurrenceDays.WORKING_HOURS_KEY_FOR_PICKUP : RecurrenceDays.WORKING_HOURS_KEY_FOR_DROPOFF, withTime);
        if(isDateAvailable) {
            // check if is should be disabled
            isDateAvailable = !shouldDisableDate(selectedDate);
        }

        // console.log('DateTimePickerWithSlider: useEffect: selectedDate: ', selectedDate, ' isDateAvailable: ', isDateAvailable);
        
        setIsDateAvailable(isDateAvailable);

        if(isStartDatePicker) {
            dispatch(setFromPickerError(!isDateAvailable));
        } else {
            dispatch(setToPickerError(!isDateAvailable));
        }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDate, maxDate])

    const extraSxPropsForTextField = withTime ? {} : { minWidth: '135px' };

    return (
        <LocalizationProvider
            dateAdapter={AdapterDayjs}
            adapterLocale={pickersLocale.adapterLocale}
            localeText={pickersLocale.localeText}
        >
            <DateTimePicker
                open={open}
                onOpen={() => setOpen(true)}
                onClose={() => setOpen(false)}
                label={label}
                value={selectedDate}
                shouldDisableDate={shouldDisableDate}
                closeOnSelect={false}
                minutesStep={15}
                format={DateTimeFormatConfig.getPickerDateFormat(withTime)}
                inputFormat={DateTimeFormatConfig.getPickerDateFormat(withTime)}
                onChange={onDayChanged}
                onMonthChange={handleMonthChanged}
                minDateTime={minDateTime}
                maxDate={maxDate}
                slots={component}
                slotProps={{
                    actionBar: {
                        selectedDate: selectedDate,
                        minDate: minDateTime,
                        minutes: selectedDate.hour() * 60 + selectedDate.minute(),
                        onTimeChanged: onTimeChanged,
                        isStartDatePicker: isStartDatePicker,
                        openingTimes: openingTimes,
                        availabilitySegments: selectedDateAvailability?.segments || []
                    },
                    textField: {
                        sx: { input: { cursor: 'pointer',  }, ...extraSxPropsForTextField },
                        readOnly: true,
                        error: !isDateAvailable,
                        onClick: (e) => setOpen(!open)
                    },
                    tabs: {
                        hidden: true,
                    },
                }}
                
                views={['day']}
                // the toolbar shows on mobile the edit icon and other controlls, to hide it here is set an empty toolbar
                ToolbarComponent={(props) => (<Fragment></Fragment>)}
            />
        </LocalizationProvider>
    );
};
